import * as React from 'react'
import { Image, Text, View, StyleSheet, Platform, Dimensions } from 'react-native'
import Formatter from '../../../helpers/Formatter'
import QRCode from 'react-native-qrcode-svg'
import { useAppSelector } from '../../../state/hooks'
import { add, sub } from 'date-fns'

export function StarReceiptView({ orderData }) {
	const { loadedData } = useAppSelector(state => state.data)
	const { orderNumber } = useAppSelector(state => state.checkout)
	const { webOrdersEnabled } = useAppSelector(state => state.settings)

	const orgName = loadedData[global.org].name ? loadedData[global.org].name : ''
	const orgAddress = loadedData[global.org].address ? loadedData[global.org].address : ''
	const orgURL = loadedData[global.org].webURL
		? loadedData[global.org].webURL.replace(/^(http:\/\/|https:\/\/)/, '')
		: ''
	let orgPhone = loadedData[global.org].phoneNumber ? loadedData[global.org].phoneNumber : ''

	if (orgPhone !== '') {
		if (orgPhone.length === 11 && orgPhone[0] === '1') {
			orgPhone = orgPhone.slice(1)
		}
		const match = orgPhone.match(/^(\d{3})(\d{3})(\d{4})$/)
		if (match) {
			orgPhone = '(' + match[1] + ')-' + match[2] + '-' + match[3]
		}
	}

	const orderType: string = orderData.isDineIn
		? 'dineIn'
		: orderData.isDelivery
			? 'delivery'
			: 'takeOut'
	const appType: string = Platform.OS === 'web' ? 'web' : global.kiosk ? 'kiosk' : 'mobile'

	const orgLogo = loadedData[global.org].logo ? loadedData[global.org].logo : ''

	const orgLogoDark = loadedData[global.org].logoDark ? loadedData[global.org].logoDark : ''

	let pickupDate = null
	if (orderData.isDelivery && orderData.cartPrep.isScheduled) {
		pickupDate = sub(new Date(orderData.cartPrep.deliveryTime), {
			minutes: orderData.cartPrep.deliveryPrepTime - orderData.cartPrep.longestPrepTime,
		}).toISOString()
	} else if (orderData.cartPrep.isScheduled) {
		pickupDate = orderData.cartPrep.pickupTime
	} else {
		pickupDate = add(new Date(), { minutes: orderData.cartPrep.longestPrepTime }).toISOString()
	}

	return (
		<View style={styles.container}>
			<Image
				source={{ uri: orgLogoDark ? orgLogoDark : orgLogo }}
				style={{ alignSelf: 'center', height: 200, width: 300 }}
				resizeMode="contain"
			/>
			<Text style={styles.title}>{orgName}</Text>
			<Text style={styles.centerText}>{orgAddress}</Text>
			<Text style={[styles.centerText, { marginBottom: 30 }]}>{orgPhone}</Text>
			<Text
				style={styles.centerText}
			>{`${orderType.toUpperCase()} ORDER VIA ${appType.toUpperCase()}`}</Text>
			<Text style={styles.centerText}>Order date: {new Date().toLocaleString()}</Text>
			{orderData.pickupDate && <Text style={styles.centerText}>Pickup time: {pickupDate}</Text>}

			<Text style={[styles.centerText, { marginTop: 40, marginBottom: 20 }]}>
				{orderData.tableNumber === '' || orderData.tableNumber === null
					? 'ORDER #' + orderNumber
					: 'ORDER #' + orderNumber + ' for ' + orderData.tableNumber}
			</Text>
			<Text style={styles.centerText}>{'************************************************'}</Text>
			<View style={styles.objects}>
				{orderData.items.map((item, index) => (
					<View key={item.name + index} style={{ marginBottom: 10 }}>
						<View key={index} style={styles.itemContainer}>
							<Text style={styles.itemName}>{`${item.qty || 0}x ${item.name || ''}`}</Text>
							{item.total != null && item.total !== undefined && (
								<Text style={styles.itemPrice}>
									{Formatter.currencyFormat((item.total || 0) / 100)}
								</Text>
							)}
						</View>
						{item.mods.length > 0 && (
							<View style={styles.modifiersContainer}>
								{item.mods.map((modifier, mIndex) => (
									<View key={mIndex + '_mod_' + modifier.name} style={styles.modifier}>
										<Text style={styles.modifierText}>+ {modifier.name}</Text>
										<Text style={styles.modifierText}>
											{Formatter.currencyFormat(modifier.priceMoney.amount / 100)}
										</Text>
									</View>
								))}
							</View>
						)}
					</View>
				))}
				{orderData.prices.subTotal !== 0 && (
					<View key={'star_subtotal'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Subtotal</Text>
						<Text style={styles.amounts}>
							{Formatter.currencyFormat(orderData.prices.subTotal)}
						</Text>
					</View>
				)}
				{orderData.prices.tip !== 0 && (
					<View key={'star_tip'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Tip</Text>
						<Text style={styles.amounts}>{Formatter.currencyFormat(orderData.prices.tip)}</Text>
					</View>
				)}
				{orderData.prices.serviceCharge !== 0 && (
					<View key={'star_sc'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Service Charge</Text>
						<Text style={styles.amounts}>
							{Formatter.currencyFormat(
								Math.round(orderData.prices.serviceCharge * 100) / 100 +
									Math.round(orderData.prices.serviceChargeGST * 100) / 100 +
									Math.round(orderData.prices.serviceChargePST * 100) / 100
							)}
						</Text>
					</View>
				)}
				{orderData.prices.bagFee + orderData.prices.bagFeeTax !== 0 && (
					<View key={'star_bagfee'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Bag Fee</Text>
						<Text style={styles.amounts}>
							{Formatter.currencyFormat(
								(orderData.prices.bagFee + orderData.prices.bagFeeTax) / 100
							)}
						</Text>
					</View>
				)}
				{orderData.prices.discount !== 0 && (
					<View key={'star_discount'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Discount</Text>
						<Text style={styles.amounts}>
							{Formatter.currencyFormat(orderData.prices.discount)}
						</Text>
					</View>
				)}
				<View key={'star_taxes'} style={styles.itemContainer}>
					<Text style={styles.amounts}>Taxes</Text>
					<Text style={styles.amounts}>{Formatter.currencyFormat(orderData.prices.taxes)}</Text>
				</View>

				{orderData.prices.total !== 0 && (
					<View key={'star_total'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Total</Text>
						<Text style={styles.amounts}>{Formatter.currencyFormat(orderData.prices.total)}</Text>
					</View>
				)}
				<Text style={styles.centerText}>{'************************************************'}</Text>
				{webOrdersEnabled && (
					<>
						<Text style={[styles.centerText, { marginBottom: 20 }]}>Order online:</Text>

						<View style={{ alignSelf: 'center' }}>
							<QRCode value={'https://' + orgURL} size={120} color="black" />
						</View>
						<Text style={styles.centerText}>{orgURL}</Text>
					</>
				)}

				<Text style={[styles.centerText, { marginVertical: 60 }]}>
					{'Thank you for your order!'}
				</Text>
			</View>
		</View>
	)
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
		marginBottom: 100,
		width: 1000,
		marginLeft: -225,
	},
	objects: {
		marginHorizontal: 225,
		marginVertical: 10,
	},
	title: {
		fontSize: 36,
		textAlign: 'center',
		marginVertical: 40,
	},
	itemContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	itemName: {
		fontSize: 24,
		fontWeight: '300',
		paddingRight: 20,
	},
	itemPrice: {
		fontSize: 24,
		fontWeight: '300',
	},
	modifiersContainer: {
		marginTop: 5,
		marginLeft: 20,
	},
	modifier: {
		justifyContent: 'space-between',
		flexDirection: 'row',
	},
	modifierText: {
		fontSize: 22,
		color: '#555',
		fontWeight: '400',
	},
	amounts: {
		fontSize: 24,
		marginTop: 5,
		fontWeight: '300',
	},

	centerText: {
		fontSize: 24,
		textAlign: 'center',
		marginBottom: 10,
		fontWeight: '300',
	},
})
